import * as React from "react";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { Objects } from "../../../../../../../react-core/Utils/Objects";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { incomePaymentDetailsLabels, incomePaymentDetailsMessages } from "../../Resource/Contents";
import { getBankAccountOptions } from "../../Resource/Helper";

interface IBankDetailsProps extends IProcessComponentProps {
    data: DrawdownPaymentDestinationAllocation[];
    onChange: (changes) => void;
    allBankAccounts: any;
}
interface IBankDetailsState {
    shouldSplitAcrossBankaccount: boolean;
}

export class BankDetails extends ProcessComponent<IBankDetailsProps, IBankDetailsState> {
    constructor(props: any) {
        super(props);
        this.state = { shouldSplitAcrossBankaccount: false };
    }

    public componentDidMount() {
        super.componentDidMount();
        if (!isNullOrEmpty(this.props.allBankAccounts) && this.props.allBankAccounts.length == 1) {
            this.onBankAccountChange(0, this.props.allBankAccounts[0].BankAccountId);
        }
    }

    private handleChange(changes) {
        const { onChange } = this.props;
        onChange(changes);
    }

    public render(): JSX.Element {
        const { data, allBankAccounts } = this.props;
        const bankAccountOptions: Map<number, string> = getBankAccountOptions(allBankAccounts);
        return (
            <>
                <div className="h4 mb-10" data-test="">
                    {incomePaymentDetailsLabels.selectBankAccount}
                </div>
                {bankAccountOptions.size > 1 && (
                    <div className="row">
                        <div className="col-12">
                            <CheckboxEditor
                                label={incomePaymentDetailsLabels.splitAcrossTwoBankAccounts}
                                value={this.state.shouldSplitAcrossBankaccount || false}
                                onChange={(v) => {
                                    this.setState({ shouldSplitAcrossBankaccount: v });
                                }}
                                cssWrapper="square-checkbox"
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-6">
                        <SelectEditor
                            label={
                                this.state.shouldSplitAcrossBankaccount ? incomePaymentDetailsLabels.bankAccount1 : ""
                            }
                            options={this.getBankAccounts(bankAccountOptions, true)}
                            selected={!isNullOrEmpty(data) ? data[0]?.BankAccountId : null}
                            getText={(v) => {
                                return bankAccountOptions.get(v);
                            }}
                            onChange={(v) => {
                                this.onBankAccountChange(0, v);
                            }}
                            placeholder="Please select"
                            required={"Please select bank acocunt"}
                        />
                    </div>
                </div>
                {this.state.shouldSplitAcrossBankaccount && (
                    <>
                        <div className="row">
                            <div className="col-md-4 col-12">
                                <PercentageTextBoxEditor
                                    label={incomePaymentDetailsLabels.proportionOfIncome}
                                    value={data[0]?.Allocation}
                                    atMost={{
                                        errorMessage: incomePaymentDetailsMessages.percentageOver100,
                                        value: 100,
                                    }}
                                    atLeast={{
                                        errorMessage: incomePaymentDetailsMessages.percentageUnder0,
                                        value: 0,
                                    }}
                                    onChange={(v) => {
                                        this.onPortionIncomeChange(0, v);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <SelectEditor
                                    label={
                                        this.state.shouldSplitAcrossBankaccount
                                            ? incomePaymentDetailsLabels.bankAccount1
                                            : ""
                                    }
                                    options={this.getBankAccounts(bankAccountOptions, false)}
                                    selected={!isNullOrEmpty(data) ? data[1]?.BankAccountId : null}
                                    getText={(v) => {
                                        return bankAccountOptions.get(v);
                                    }}
                                    onChange={(v) => {
                                        this.onBankAccountChange(1, v);
                                    }}
                                    placeholder="Please select"
                                    required={"Please select bank account"}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4 col-12">
                                <PercentageTextBoxEditor
                                    label={incomePaymentDetailsLabels.proportionOfIncome}
                                    value={data[1]?.Allocation}
                                    atMost={{
                                        errorMessage: incomePaymentDetailsMessages.percentageOver100,
                                        value: 100,
                                    }}
                                    atLeast={{
                                        errorMessage: incomePaymentDetailsMessages.percentageUnder0,
                                        value: 0,
                                    }}
                                    onChange={(v) => {
                                        this.onPortionIncomeChange(1, v);
                                    }}
                                    validationRules={[
                                        {
                                            errorMessage: incomePaymentDetailsMessages.totalProportionOfIncomeNot100,
                                            validator: (v) => v + data[0]?.Allocation === 100,
                                        },
                                    ]}
                                />
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }

    getBankAccounts(bankAccountOptions: any, isPrimaryBankAccount: boolean) {
        const { data } = this.props;
        const keyList: number[] = Array.from(bankAccountOptions.keys());
        const index = isPrimaryBankAccount ? 1 : 0;
        return !this.state.shouldSplitAcrossBankaccount
            ? keyList
            : keyList.filter((key) => key !== data[index]?.BankAccountId);
    }

    private onPortionIncomeChange(position: number, value: number) {
        const data = Objects.cloneObject(this.props.data);
        if (data[position]) {
            data[position].Allocation = value;
            this.handleChange(data);
        }
    }

    private onBankAccountChange(position: number, bankAccountId: number) {
        let updatedData = Objects.cloneObject(this.props.data);
        if (!bankAccountId) {
            updatedData = updatedData.splice(position, 1);
            this.handleChange(updatedData);
            return;
        }
        const bankAccountInfo = this.props.allBankAccounts.filter(
            (bankAccount) => bankAccount.BankAccountId === bankAccountId
        )[0];
        const isAlreadyAdded = this.props.data.some((bankAccount) => bankAccount.BankAccountId === bankAccountId);
        if (bankAccountInfo && !isAlreadyAdded) {
            const bankDetail: DrawdownPaymentDestinationAllocation = {
                BankAccountId: bankAccountInfo.BankAccountId,
                Destination: IncomePaymentDestination.BankAccount,
                Allocation: isNullOrEmpty(updatedData) || !this.state.shouldSplitAcrossBankaccount ? 100 : 0,
            };
            if (
                (!isNullOrEmpty(updatedData) && this.state.shouldSplitAcrossBankaccount) ||
                isNullOrEmpty(updatedData)
            ) {
                updatedData.push(bankDetail);
            } else {
                updatedData[0] = bankDetail;
            }
        }
        this.handleChange(updatedData);
    }
}
export default BankDetails;
